import type {App} from 'vue'
import type {Router} from 'vue-router'
import type {Pinia} from 'pinia'
import {createPinia} from 'pinia'
import {PiniaColada} from '@pinia/colada'
import {createHead} from '@unhead/vue/client'
import {InferSeoMetaPlugin} from '@unhead/vue/plugins'
import VueChartkick from 'vue-chartkick'
import 'chartkick/chart.js'
import {ApiError} from '@/lib/ApiError'
import {installAnalytics} from '@/lib/analytics'
import {installAssetReloadGuard} from '@/lib/assetReloadGuard'
import type {AnalyticsSite} from '@/lib/analytics'
import './theme.css'

// Initial dark mode detection (system preference) — once the user loads,
// useTheme() in App.vue takes over and respects the user's app_theme setting.
import {applyTheme} from '@/lib/theme'
import {createSentryPiniaPlugin} from "@sentry/vue";

interface SetupSpaOptions {
    hasLoggedOutRoutes?: boolean
    router: Router
    // Which frontend this is, for Google Tag Manager. Matches the `site` the server-rendered
    // GTM snippet seeds onto the dataLayer, so tags can be scoped to one app or the other.
    site: AnalyticsSite
    // Runs after plugins are installed but before app.mount — e.g. the www entry seeds the
    // Pinia Colada query cache from a prerendered snapshot's serialized state here.
    beforeMount?: (app: App, pinia: Pinia) => void
}

applyTheme('system')

export default async function setupSpa(app: App, {hasLoggedOutRoutes = false, router, site, beforeMount }: SetupSpaOptions): Promise<App> {
    const pinia = createPinia()
    pinia.use(createSentryPiniaPlugin());

    // www: og:title/og:description mirror each page's resolved title + meta description, so
    // shared links preview the actual page. Explicit page-level og tags still win (the plugin's
    // inferred tags are low priority). Keep in sync with frontend/ssr/www.ts — crawlers read
    // the prerendered head, so the SSR head instance needs the same plugin.
    const head = createHead({plugins: site === 'www' ? [InferSeoMetaPlugin()] : []})

    app.use(pinia)
    app.use(PiniaColada)
    app.use(head)
    app.use(VueChartkick)

    app.use(router)

    // Global error handler — logs errors and surfaces unexpected ones
    app.config.errorHandler = (err) => {
        // ApiErrors from queries are handled by ErrorState in components — don't log
        if (err instanceof ApiError) return
        console.error(`[Vue Error]:`, err)
    }

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason instanceof ApiError) return
        console.error('[Unhandled Rejection]', event.reason)
    })

    // Reloads once when a lazy chunk 404s after a deploy, then falls back to an error message
    // rather than reloading forever against an asset that is genuinely gone.
    installAssetReloadGuard()

    if (hasLoggedOutRoutes) await router.isReady()

    app.config.warnHandler = (msg, instance, otherArg) => {
        if (msg.includes('Missing required prop')) {
            console.error(msg, instance, otherArg)
            throw new Error(msg)
        }
    }

    beforeMount?.(app, pinia)

    app.mount('#app')

    // After mount: the first `page_view` needs the initial view rendered to read its title.
    installAnalytics(router, site)

    return app;
}
